import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Star, Shield, Filter, Briefcase, ChevronRight, Loader2 } from "lucide-react";
import { api } from "@/api/client";
import { logger } from "@/utils/logger";

const SERVICE_TYPES = [
  { value: "", label: "All Services" },
  { value: "groomer", label: "Groomer" },
  { value: "trainer", label: "Trainer" },
  { value: "farrier", label: "Farrier" },
  { value: "boarding", label: "Boarding" },
  { value: "veterinary", label: "Veterinary" },
  { value: "transport", label: "Transport" },
];

const SPECIES = [
  { value: "", label: "All Species" },
  { value: "canine", label: "Canine" },
  { value: "feline", label: "Feline" },
  { value: "equine", label: "Equine" },
  { value: "cattle", label: "Cattle" },
  { value: "exotics", label: "Exotics" },
  { value: "farm", label: "Farm Animals" },
];

const BrowseVendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [filters, setFilters] = useState({ service_type: "", species: "", state: "", search: "" });

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString() });
      if (filters.service_type) params.set("service_type", filters.service_type);
      if (filters.species) params.set("species", filters.species);
      if (filters.state) params.set("state", filters.state);
      if (filters.search) params.set("search", filters.search);
      
      const res = await api.get(`/vendors/browse?${params.toString()}`);
      setVendors(res.data.vendors || []);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) { logger.error("Fetch error:", err); } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVendors(); }, [page, filters]);

  return (
    <div className="pt-24 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-2">Marketplace</p>
            <h1 className="text-4xl text-[#EDEDED]" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="vendors-title">
              Service Providers
            </h1>
            <p className="text-[#A1A1AA] mt-2">{total} verified providers</p>
          </div>
          <Link to="/vendor/register" className="btn-gold text-sm px-4 py-2 inline-flex items-center gap-2" data-testid="register-vendor-btn">
            <Briefcase className="w-4 h-4" /> Register
          </Link>
        </div>

        {/* Filters */}
        <div className="glass-card p-4 mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
              <input
                value={filters.search}
                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                placeholder="Search..."
                className="input-minimal w-full pl-9"
                data-testid="vendor-search"
              />
            </div>
            <select
              value={filters.service_type}
              onChange={(e) => { setFilters(f => ({ ...f, service_type: e.target.value })); setPage(1); }}
              className="input-minimal"
              data-testid="vendor-service-filter"
            >
              {SERVICE_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select
              value={filters.species}
              onChange={(e) => { setFilters(f => ({ ...f, species: e.target.value })); setPage(1); }}
              className="input-minimal"
              data-testid="vendor-species-filter"
            >
              {SPECIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <input
              value={filters.state}
              onChange={(e) => setFilters(f => ({ ...f, state: e.target.value }))}
              placeholder="State"
              className="input-minimal"
              data-testid="vendor-state-filter"
            />
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
          </div>
        ) : vendors.length === 0 ? (
          <div className="text-center py-20">
            <Briefcase className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
            <p className="text-[#A1A1AA]">No service providers found.</p>
            <p className="text-[#71717A] text-sm mt-1">Try adjusting your filters or be the first to register.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vendors.map(v => (
              <div key={v.provider_id} className="glass-card p-5 hover:border-[#D4AF37]/30 transition-colors" data-testid={`vendor-card-${v.provider_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-[#EDEDED] font-medium">{v.display_name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] capitalize">{v.service_type}</span>
                      <span className="text-xs text-[#71717A] capitalize">{Array.isArray(v.species) ? v.species.join(", ") : v.species}</span>
                    </div>
                  </div>
                  {v.is_verified && (
                    <span className="flex items-center gap-1 text-xs text-[#22C55E]">
                      <Shield className="w-3 h-3" /> Verified
                    </span>
                  )}
                </div>
                {v.description && (
                  <p className="text-sm text-[#A1A1AA] mb-3 line-clamp-2">{v.description}</p>
                )}
                <div className="flex items-center gap-4 text-xs text-[#71717A]">
                  {(v.location_city || v.location_state) && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {[v.location_city, v.location_state].filter(Boolean).join(", ")}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            {Array.from({ length: Math.min(pages, 5) }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-8 h-8 rounded-lg text-sm ${p === page ? "bg-[#D4AF37] text-black" : "bg-[#27272A] text-[#A1A1AA] hover:bg-[#3F3F46]"}`}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowseVendors;
